import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { getIndex, isInArray } from "@src/utils/array";
import {
    getInitialCharacterList,
    setCharacterList,
    transformToCharacterListVersion,
} from "@src/utils/character-utils";
import { api } from "@src/utils/axios-instance";
import { Character, CharacterListVersion, CharacterWithVersion } from "@src/types/global";
export type CharacterStore = {
    addVersionToList: (characterWithVersion: CharacterWithVersion) => void;
    updateCharacterVersionInList: (
        character: CharacterListVersion,
    ) => void;
    characterList: CharacterListVersion[];
    setCharacterList: (oldList: CharacterListVersion[]) => void;
    getCharacterById: (id: string) => Promise<Character>;
    getCharacterByIdAndVersion: (
        id: string,
        versionName: string,
    ) => Promise<CharacterWithVersion>;
    removeCharacterVersion: (versionId: string) => void;
    swapCharacterVersion: (
        character: CharacterListVersion,
        characterListVersion: CharacterListVersion,
    ) => void;
    selectedVersion: number;
    setSelectedVersion: (v: number) => void;
    selectedExpression: string;
    setSelectedExpression: (v: string) => void;
};

export const useCharacterStore = create<CharacterStore>()(
    immer((set, get) => ({
        /**
         * Takes a character with version object, updates the state by inserting it in the character list
         * @param characterWithVersion - The character version to add to the list
         */
        addVersionToList: (characterWithVersion) => {
            const currentList = get().characterList;
            const characterListVersion =
                transformToCharacterListVersion(characterWithVersion);
            if (currentList != null) {
                if (
                    isInArray(
                        characterListVersion.version_id,
                        currentList,
                        "version_id",
                    )
                ) {
                    setCharacterList(currentList);
                    return;
                }
            }
            set((state) => {
                state.characterList.push(characterListVersion);
            });

            setCharacterList(get().characterList);
        },
        updateCharacterVersionInList: (characterWithVersion) => {
            get().swapCharacterVersion(characterWithVersion, characterWithVersion)
        },
        characterList: getInitialCharacterList(),
        setCharacterList: (list) => {
            set({
                characterList: list,
            });
            setCharacterList(list);
        },
        getCharacterByIdAndVersion: async (id, versionName) => {
            const { data } = await api.get(
                "characters/" + id + "/?name=" + versionName,
            );
            return data.data;
        },

        getCharacterById: async (id) => {
            const { data } = await api.get("characters/" + id);
            return data.data;
        },
        swapCharacterVersion: (character, characterListVersion) => {
            const index = getIndex(
                character.version_id,
                get().characterList,
                "version_id",
            );
            set((state) => {
                state.characterList[index] = characterListVersion;
            });
            setCharacterList(get().characterList);
        },
        removeCharacterVersion: (versionId) => {
            set((state) => {
                state.characterList = state.characterList.filter((char) => {
                    return char.version_id !== versionId;
                });
            });

            setCharacterList(get().characterList);
        },
        selectedVersion: 0,
        setSelectedVersion: (v) => {
            set((state) => {
                state.selectedVersion = v;
            });
        },
        selectedExpression: "",
        setSelectedExpression: (v) => {
            set({
                selectedExpression: v,
            });
        },
    })),
);
